import React, { useState, useEffect, memo } from 'react';
import { Box, Typography, CircularProgress, Collapse } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Nhớ import SecureImage để render Thumbnail an toàn
import SecureImage from './SecureImage'; 

const IOS_BLUE = '#007aff';

// ============================================================================
// HELPER: PHÂN TÍCH VÀ TRẢ VỀ URL TỪ FILE ID / FILE OBJECT / URL
// ============================================================================
export const resolveFileUrl = (val, asThumbnail = false, context) => {
  if (!val) return null;
  let fileIdOrUrl = typeof val === 'object' ? (val._id || val.id || val.url) : val;
  if (!fileIdOrUrl) return null;
  if (String(fileIdOrUrl).startsWith('http')) return fileIdOrUrl; 
  
  const token = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : context.config?.public_token);
  // Dùng size=XS cho Thumbnail nhẹ nhất có thể
  const sizeParam = asThumbnail ? '&size=XS' : '';
  const slash = String(fileIdOrUrl).startsWith('/') ? '' : '/';

  if (String(fileIdOrUrl).includes('/')) {
     return `${context.config?.server_url || ''}${slash}${fileIdOrUrl}${fileIdOrUrl.includes('?') ? '&' : '?'}access_token=${token}${sizeParam}`;
  } else {
     return `${context.config?.server_url || ''}/api/${context.config?.id_app || ''}/file/download/${fileIdOrUrl}?access_token=${token}${sizeParam}`;
  }
};

// ============================================================================
// COMPONENT CHÍNH: HIỂN THỊ DANH SÁCH FILE VỚI THUMBNAIL & SIZE
// ============================================================================
const SecureFileDisplay = memo(({ value, context, sx, defaultExpanded = false }) => {
  const [resolvedFiles, setResolvedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // 1. EFFECT THỦY HÓA DỮ LIỆU (HYDRATION)
  useEffect(() => {
    const fetchFileMetadata = async () => {
      const rawArr = Array.isArray(value) ? value : [value];
      const validItems = rawArr.filter(f => f !== undefined && f !== null && f !== '');
      
      if (validItems.length === 0) {
        setResolvedFiles([]);
        return;
      }

      const idsToFetch = [];
      validItems.forEach(item => {
        if (typeof item === 'string') {
          idsToFetch.push(item);
        } else if (typeof item === 'object') {
          // Kiểm tra xem object đã có thuộc tính 'file' lồng bên trong chưa
          if (!item.file?.originalname && (item._id || item.id)) {
            idsToFetch.push(item._id || item.id);
          }
        }
      });

      if (idsToFetch.length > 0) {
        setIsLoading(true);
        try {
          const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');
          const res = await context.apis.asyncGetList(userToken, "file", {
            condition: { _id: { $in: idsToFetch } },
            limit: 100
          });
          const fetchedFiles = res?.data || res || [];

          const finalFiles = validItems.map(origItem => {
            const idToMatch = typeof origItem === 'string' ? origItem : (origItem._id || origItem.id);
            const foundObj = fetchedFiles.find(f => f._id === idToMatch);
            return foundObj || origItem; 
          });
          
          setResolvedFiles(finalFiles);
        } catch (error) {
          console.error("Lỗi fetch thông tin file hiển thị:", error);
          setResolvedFiles(validItems); 
        } finally {
          setIsLoading(false);
        }
      } else {
        setResolvedFiles(validItems);
      }
    };

    fetchFileMetadata();
  }, [value, context]);

  // 2. HÀM XỬ LÝ CLICK TẢI FILE BẢO MẬT (Chống lộ Token)
  const handleSecureDownload = async (e, url, fileName) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      context.setProgressStatus && context.setProgressStatus(true);

      /*const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);*/
      context.apis.downloadFile(url,fileName,(e)=>{
        if(e) return context.alert(e.message||e.error||e, null, "red");
        context.setProgressStatus && context.setProgressStatus(false);
      });
    } catch (err) {
      context.alert("Tải file thất bại", null, "red");
    } finally {
      context.setProgressStatus && context.setProgressStatus(false);
    }

  };

  if (resolvedFiles.length === 0 && !isLoading) return <Typography variant="body2" color="text.secondary">---</Typography>;

  return (
    <Box sx={{ mt: 0.5, ...sx }}>
      
      {/* KHỐI TIÊU ĐỀ: BẤM ĐỂ ẨN/HIỆN */}
      <Box 
        onClick={(e) => {
          e.stopPropagation(); 
          setIsExpanded(!isExpanded);
        }}
        sx={{ 
          display: 'inline-flex', alignItems: 'center', gap: 0.5, 
          cursor: 'pointer', color: IOS_BLUE, bgcolor: '#f0f8ff', 
          px: 1, py: 0.5, borderRadius: 2, userSelect: 'none',
          '&:hover': { bgcolor: '#e1f5fe' }
        }}
      >
        <AttachFileIcon sx={{ fontSize: 16 }} />
        <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.8rem' }}>
          {isLoading ? 'Đang tải thông tin...' : `${resolvedFiles.length} tệp đính kèm`}
        </Typography>
        {!isLoading && (
          isExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />
        )}
      </Box>

      {/* DANH SÁCH FILE VỚI THUMBNAIL */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pl: 1 }}>
            <CircularProgress size={14} sx={{ color: IOS_BLUE }} />
            <Typography variant="caption" color="text.secondary">Đang lấy dữ liệu tệp...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {resolvedFiles.map((fObj, i) => {
               
               // 1. Trích xuất Tên, Đuôi file và Dung lượng từ object `file`
               const fileName = fObj.file?.originalname || fObj.originalname || fObj.file_name || fObj.name || `Tập tin đính kèm ${i+1}`;
               const fileExt = (fObj.file?.extension || fileName.split('.').pop()).toLowerCase();
               const fileSize = fObj.file?.size || fObj.size;
               
               // 2. Format dung lượng sang MB hoặc KB
               let formattedSize = '';
               if (fileSize) {
                 formattedSize = fileSize > 1024 * 1024 
                   ? `${(fileSize / 1024 / 1024).toFixed(2)} MB` 
                   : `${(fileSize / 1024).toFixed(0)} KB`;
               }

               const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);
               const fileId = fObj._id || fObj.id || fObj;

               const fullDownloadUrl = resolveFileUrl(fileId, false, context);
               const thumbnailUrl = isImage ? resolveFileUrl(fileId, true, context) : null;

               if (!fullDownloadUrl) return null;

               return (
                 <Box 
                    key={fileId} 
                    sx={{ 
                      display: 'flex', alignItems: 'center', p: 0.8, pr: 1.5,
                      border: '1px solid #e5e5ea', borderRadius: 2, bgcolor: '#fff',
                      // SỬA LỖI 2A: Đổi từ fit-content sang 100% để nó co giãn theo cha, không chọc thủng cha
                      width: '100%', 
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                 >
                    {/* KHU VỰC THUMBNAIL HOẶC ICON */}
                    {thumbnailUrl ? (
                      <SecureImage 
                        src={thumbnailUrl} 
                        alt={fileName}
                        // Style bây giờ đã hoạt động chuẩn xác 12px
                        style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, marginRight: 12, flexShrink: 0, border: '1px solid #e5e5ea', backgroundColor: '#f2f2f7' }} 
                      />
                    ) : (
                      <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1.5, mr: 1.5, bgcolor: '#f2f2f7', flexShrink: 0 }}>
                        <AttachFileIcon sx={{ color: '#8e8e93', fontSize: 18 }} />
                      </Box>
                    )}

                    {/* KHU VỰC TEXT (TÊN FILE & DUNG LƯỢNG) */}
                    {/* SỬA LỖI 2B: Thêm minWidth: 0 và flexGrow: 1 vào thẻ cha chứa text */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, flexGrow: 1 }}>
                      <Typography 
                        variant="body2" 
                        noWrap={false}
                        component="a" 
                        onClick={(e) => handleSecureDownload(e, fullDownloadUrl, fileName)}
                        sx={{ 
                          color: IOS_BLUE, fontWeight: 500, fontSize: '0.85rem',
                          cursor: 'pointer', textDecoration: 'none', 
                          display: 'block', // Bắt buộc phải có display block với thẻ <a> để noWrap chạy
                          '&:hover': { textDecoration: 'underline' } 
                        }}
                      >
                        {fileName}
                      </Typography>
                      
                      {formattedSize && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, lineHeight: 1, mt: 0.2 }}>
                          <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#d1d1d6' }} />
                          {formattedSize}
                        </Typography>
                      )}
                    </Box>
                 </Box>
               )
            })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
});

export default SecureFileDisplay;